import React, { useEffect, useId, useRef, useState } from 'react';

export interface UserMenuProps {
  userName: string;
  isAdmin?: boolean;
  signOutLabel?: string;
  adminLabel?: string;
  profileLabel?: string;
  termsLabel?: string;
  onSignOut: () => void;
  onOpenAdmin: () => void;
  onOpenProfile?: () => void;
  onOpenTerms?: () => void;
}

const UserIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
    <circle cx="12" cy="8" r="3.4" stroke="currentColor" strokeWidth="1.7" />
    <path
      d="M5.5 19.2c.7-3.1 3.2-5 6.5-5s5.8 1.9 6.5 5"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
);

const ChevronIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
    <path d="m7 14 5-5 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const UserMenu: React.FC<UserMenuProps> = ({
  userName,
  isAdmin = false,
  signOutLabel = 'Sign out',
  adminLabel = 'Admin',
  profileLabel = 'Profile settings',
  termsLabel = 'Terms of use',
  onSignOut,
  onOpenAdmin,
  onOpenProfile,
  onOpenTerms,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const runAction = (action?: () => void) => {
    setOpen(false);
    action?.();
  };

  return (
    <div className="app-user-menu" ref={containerRef}>
      <button
        type="button"
        className={`app-user-menu-trigger ${open ? 'is-open' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`User menu for ${userName || 'account'}`}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="app-user-menu-avatar" aria-hidden="true">
          <UserIcon />
        </span>
        <span className="app-user-menu-name">{userName || 'Account'}</span>
        <span className={`app-user-menu-chevron ${open ? 'is-open' : ''}`} aria-hidden="true">
          <ChevronIcon />
        </span>
      </button>

      {open ? (
        <div className="app-user-menu-panel" id={menuId} role="menu" aria-label="User menu">
          <button
            type="button"
            role="menuitem"
            className="app-user-menu-item app-user-menu-item-danger"
            onClick={() => runAction(onSignOut)}
          >
            {signOutLabel}
          </button>
          {isAdmin ? (
            <button type="button" role="menuitem" className="app-user-menu-item" onClick={() => runAction(onOpenAdmin)}>
              {adminLabel}
            </button>
          ) : null}
          <button type="button" role="menuitem" className="app-user-menu-item" onClick={() => runAction(onOpenProfile)}>
            {profileLabel}
          </button>
          <button type="button" role="menuitem" className="app-user-menu-item" onClick={() => runAction(onOpenTerms)}>
            {termsLabel}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default UserMenu;
